import React from 'react';
import { Tooltip, colors } from '@insurancemenu/imenu-components-lib';
import { AlignmentTypes, ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types';
import { SubscriberErrorMessageType } from 'types/electionService';
import { TotalRateCalculationResult } from 'global/utils/election/productLineValidation';
import { getUniqueProductLinesFromArray } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import {
  InvalidTotalRateStyled,
  InvalidPLinesStyled,
  ErrorOutlineStyled,
  TooltipErrorListStyled,
  TooltipErrorItemStyled,
} from './styles';
import { SUB_TOTAL_RATE_ERROR_TOOLTIP_TEST_ID } from '../../constants';

interface Props {
  totalRateCalculationResult: TotalRateCalculationResult;
}

interface TooltipErrorItemProps {
  error: SubscriberErrorMessageType;
  incompleteProductLineIds: ProductLineId[];
  productLineIdsMissingSelection: ProductLineId[];
}

const ERROR_LABELS: Record<SubscriberErrorMessageType, string> = {
  INCOMPLETE_PROFILE: 'Incomplete profile',
  MISSING_PRODUCT_LINE_ANSWERS: 'Missing answers',
  MISSING_DEPENDENT_PRODUCT_LINE_SELECTION: 'Election required',
};

const TooltipErrorItem: React.FC<TooltipErrorItemProps> = ({
  error,
  incompleteProductLineIds,
  productLineIdsMissingSelection,
}) => {
  switch (error) {
    case 'INCOMPLETE_PROFILE':
      return <TooltipErrorItemStyled>Update missing information in the Employee Profile</TooltipErrorItemStyled>;

    case 'MISSING_PRODUCT_LINE_ANSWERS':
      return (
        <TooltipErrorItemStyled>
          Complete required fields for
          <InvalidPLinesStyled>{getUniqueProductLinesFromArray(incompleteProductLineIds)}</InvalidPLinesStyled>
          to calculate rates
        </TooltipErrorItemStyled>
      );

    case 'MISSING_DEPENDENT_PRODUCT_LINE_SELECTION':
      return (
        <TooltipErrorItemStyled>
          Make selection for
          <InvalidPLinesStyled>{getUniqueProductLinesFromArray(productLineIdsMissingSelection)}</InvalidPLinesStyled>
          to proceed
        </TooltipErrorItemStyled>
      );

    default:
      return null;
  }
};

const TotalValidationTooltip: React.FC<Props> = ({
  totalRateCalculationResult: { incompleteProductLineIds, productLineIdsMissingSelection, errorTypes },
}) => {
  const hasMultipleErrors = errorTypes.length > 1;
  const errorTooltipLabel = hasMultipleErrors ? 'Multiple issues' : ERROR_LABELS[errorTypes[0]];

  return (
    <Tooltip
      alignment={AlignmentTypes.BOTTOM_END}
      data-test-id={SUB_TOTAL_RATE_ERROR_TOOLTIP_TEST_ID}
      label={
        <TooltipErrorListStyled $hasMultipleErrors={hasMultipleErrors}>
          {errorTypes.map((error) => (
            <TooltipErrorItem
              key={error}
              error={error}
              incompleteProductLineIds={incompleteProductLineIds}
              productLineIdsMissingSelection={productLineIdsMissingSelection}
            />
          ))}
        </TooltipErrorListStyled>
      }
    >
      <InvalidTotalRateStyled>
        <ErrorOutlineStyled width="2rem" color={colors.red[600]} />
        {errorTooltipLabel}
      </InvalidTotalRateStyled>
    </Tooltip>
  );
};

export default TotalValidationTooltip;
